import {AlertTriangleIcon, CheckCircle2Icon, Loader2Icon} from "lucide-react";
import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import {Badge} from "@components/ui/badge";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@components/ui/table";
import {ToggleGroup, ToggleGroupItem} from "@components/ui/toggle-group";
import {
    type EohAlertRow,
    type EohChartSeries,
    type EohChurnRow,
    type EohClientTrendRow,
    type EohClientVolumeRow,
    type EohConcentrationRow,
    type EohDailyActivityRow,
    type EohFillRateRow,
    type EohForwardRow,
    type EohPivotRow,
    type EohSnapshotKpis,
    type EohUnfillRow,
    type EohWeeklyRow,
    type EohYoYRow,
    useEohBookingsSummary,
} from "@hooks/use-vnext";

interface TabProps {
    workspaceId: string;
    scenarioId: string;
}

function fmtMonth(key: string): string {
    const [y, m] = key.split("-");
    const date = new Date(Number(y), Number(m) - 1);
    return date.toLocaleDateString("en-AU", {month: "short", year: "2-digit"});
}

function fmtWeek(key: string): string {
    if (!key) return "—";
    const d = new Date(key + "T00:00:00");
    const day = d.getDate();
    const mon = d.toLocaleDateString("en-AU", {month: "short"});
    return `${day} ${mon}`;
}

function fmtDate(key?: string): string {
    if (!key) return "—";
    const d = new Date(key + "T00:00:00");
    return d.toLocaleDateString("en-AU", {day: "2-digit", month: "short", year: "2-digit"});
}

function fmtFyWeek(key: string): string {
    const wk = key.split("-W")[1];
    return wk ? `W${wk}` : key;
}

function fmtMonthNum(mm: string): string {
    const date = new Date(2000, Number(mm) - 1);
    return date.toLocaleDateString("en-AU", {month: "short"});
}

function fmtNum(v: number, decimals = 0): string {
    return v.toLocaleString("en-AU", {minimumFractionDigits: decimals, maximumFractionDigits: decimals});
}

function fmtDollar(v: number): string {
    return "$" + fmtNum(v, 0);
}

function Dash() {
    return <span className="text-muted-foreground/40">–</span>;
}

function SectionHeader({title, subtitle}: {title: string; subtitle: string}) {
    return (
        <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
    );
}

function SectionCard({
    title, subtitle, children,
}: {
    title: string;
    subtitle: string;
    children: React.ReactNode;
}) {
    return (
        <Card className="gap-0 rounded-lg py-0">
            <CardHeader className="px-4 py-3">
                <CardTitle className="text-sm">{title}</CardTitle>
                <p className="text-xs text-muted-foreground">{subtitle}</p>
            </CardHeader>
            <CardContent className="max-h-[420px] overflow-auto px-4 pb-4 [&_[data-slot=table-container]]:overflow-visible">
                {children}
            </CardContent>
        </Card>
    );
}

const stickyHeaderClass = "[&_th]:sticky [&_th]:top-0 [&_th]:z-20 [&_th]:bg-muted [&_th]:shadow-sm";
const stickyLeftHeaderClass = "sticky left-0 z-30 bg-muted";
const stickyLeftCellClass = "sticky left-0 z-10 bg-background";

function pctBadge(pct: number) {
    const variant = pct >= 80 ? "default" : pct >= 60 ? "secondary" : "destructive";
    return <Badge variant={variant}>{pct}%</Badge>;
}

function fmtCompact(v: number): string {
    return Intl.NumberFormat("en-AU", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(v);
}

function KpiCard({
    label, value, subtext, tone = "neutral",
}: {
    label: string;
    value: string;
    subtext?: string;
    tone?: "neutral" | "good" | "warn" | "bad";
}) {
    const valueColor =
        tone === "good"
            ? "text-green-600"
            : tone === "warn"
              ? "text-[#FF5100]"
              : tone === "bad"
                ? "text-red-600"
                : "text-[#209DB3]";

    return (
        <Card className={tone === "warn" || tone === "bad" ? "border-amber-300 bg-white shadow-sm" : "bg-white shadow-sm"}>
            <CardContent className="p-5 text-center">
                <p className={`text-2xl font-extrabold tabular-nums ${valueColor}`}>
                    {value}
                </p>
                <p className="mt-1 text-sm font-bold leading-snug text-[#0F172A]">{label}</p>
                {subtext && <p className="mt-1 text-[11px] text-muted-foreground">{subtext}</p>}
            </CardContent>
        </Card>
    );
}

function BookingsSnapshot({kpis}: {kpis: EohSnapshotKpis}) {
    const syncedAt = kpis.latest_shift_synced_at ? fmtDate(kpis.latest_shift_synced_at.slice(0, 10)) : "—";
    const fillTone = kpis.current_week_fill_pct >= 80 ? "good" : kpis.current_week_fill_pct >= 60 ? "warn" : "bad";
    const cancellationTone = kpis.current_week_cancellations > 0 ? "warn" : "neutral";
    return (
        <section className="space-y-3">
            <div>
                <h3 className="mb-1 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Bookings Snapshot</h3>
                <p className="text-xs text-muted-foreground">
                    Current FY week {kpis.current_fy_week || "—"} · EOH shifts last synced {syncedAt}
                </p>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
                <KpiCard label="Future Shifts" value={fmtNum(kpis.future_booked_shifts)} subtext="Booked from today" tone="neutral" />
                <KpiCard label="Future Hours" value={fmtNum(kpis.future_booked_hours, 1)} subtext="Booked hours" tone="neutral" />
                <KpiCard label="Est. Revenue" value={fmtDollar(kpis.future_est_revenue)} subtext="At current display rate" />
                <KpiCard label="Current Fill" value={`${kpis.current_week_fill_pct}%`} subtext={kpis.current_fy_week} tone={fillTone} />
                <KpiCard label="Cancellations" value={fmtNum(kpis.current_week_cancellations)} subtext="Current FY week" tone={cancellationTone} />
                <KpiCard label="Active Clients" value={fmtNum(kpis.active_clients_this_week)} subtext="Current FY week" />
            </div>
        </section>
    );
}

function ExecutiveSummary({
    summary, alerts,
}: {
    summary: string[];
    alerts: EohAlertRow[];
}) {
    return (
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
            <Card className="gap-3 rounded-lg py-4">
                <CardHeader className="px-4">
                    <CardTitle className="text-sm">Executive Summary</CardTitle>
                </CardHeader>
                <CardContent className="px-4">
                    {summary.length ? (
                        <ul className="space-y-2 text-sm">
                            {summary.map((item) => (
                                <li key={item} className="flex gap-2">
                                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#209DB3]" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-muted-foreground">No booking summary available yet.</p>
                    )}
                </CardContent>
            </Card>
            <Card className="gap-3 rounded-lg py-4">
                <CardHeader className="px-4">
                    <CardTitle className="text-sm">Watchlist</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 px-4">
                    {alerts.map((alert) => (
                        <div key={`${alert.title}-${alert.detail}`} className="rounded-md border bg-background p-3">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                {alert.severity === "warning" ? (
                                    <AlertTriangleIcon className="h-4 w-4 text-amber-600" />
                                ) : (
                                    <CheckCircle2Icon className="h-4 w-4 text-[#209DB3]" />
                                )}
                                {alert.title}
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">{alert.detail}</p>
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}

const chartColors = ["#209DB3", "#3A6B8F", "#7C9A3D", "#D88A3D", "#9D5C63", "#6B7280"];

function ChartCard({
    title, children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Card className="gap-3 rounded-lg py-4">
            <CardHeader className="px-4">
                <CardTitle className="text-sm">{title}</CardTitle>
            </CardHeader>
            <CardContent className="h-[220px] px-3">{children}</CardContent>
        </Card>
    );
}

function BookingsCharts({series}: {series: EohChartSeries}) {
    const forward = series.forward_hours_by_week ?? [];
    const fill = series.fill_rate_by_week ?? [];
    const status = series.status_mix ?? [];
    const clients = series.top_clients_by_hours ?? [];
    return (
        <section className="space-y-3">
            <SectionHeader title="Booking Trends" subtitle="High-level chart view before the detailed tables." />
            <div className="grid gap-4 xl:grid-cols-2">
                <ChartCard title="Forward Hours by FY Week">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={forward} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis fontSize={11} tickFormatter={(v) => fmtCompact(Number(v))} width={42} />
                            <Tooltip formatter={(v) => [fmtNum(Number(v), 1), "Hours"]} labelFormatter={(label) => `W${label}`} />
                            <Bar dataKey="hours" fill="#209DB3" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Fill Rate by FY Week">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={fill} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={38} />
                            <Tooltip formatter={(v) => [`${v}%`, "Fill rate"]} labelFormatter={(label) => `W${label}`} />
                            <Line type="monotone" dataKey="fill_pct" stroke="#209DB3" strokeWidth={2} dot={{r: 2}} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Booking Status Mix">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={status} dataKey="shifts" nameKey="status" innerRadius={48} outerRadius={82} paddingAngle={2}>
                                {status.map((_entry, index) => (
                                    <Cell key={`status-${index}`} fill={chartColors[index % chartColors.length]} />
                                ))}
                            </Pie>
                            <Legend iconSize={8} wrapperStyle={{fontSize: 11}} />
                            <Tooltip formatter={(v) => [fmtNum(Number(v)), "Shifts"]} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Top Clients by Hours">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={clients.slice(0, 8)} layout="vertical" margin={{top: 4, right: 16, left: 4, bottom: 0}}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" fontSize={11} tickFormatter={(v) => fmtCompact(Number(v))} />
                            <YAxis type="category" dataKey="client" width={145} fontSize={10} tickLine={false} axisLine={false} />
                            <Tooltip formatter={(v) => [fmtNum(Number(v), 1), "Hours"]} />
                            <Bar dataKey="hours" fill="#3A6B8F" radius={[0, 4, 4, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </section>
    );
}

function DailyActivityTable({rows}: {rows: EohDailyActivityRow[]}) {
    if (!rows.length) return null;
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-right">Filled</TableHead>
                        <TableHead className="text-right">Cancelled</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Clients</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.date}>
                            <TableCell className="font-medium text-sm">{fmtDate(row.date)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(row.requested)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(row.filled)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(row.cancelled)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(row.hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtDollar(row.revenue)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(row.active_clients)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Pivot table ─────────────────────────────────────────────

function PivotTable({
    rows, months, keyField, isFloat = false,
}: {
    rows: EohPivotRow[]; months: string[]; keyField: string; isFloat?: boolean;
}) {
    const decimals = isFloat ? 1 : 0;
    const grandTotal = rows.reduce((s, r) => s + r.total, 0);
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className={`${stickyLeftHeaderClass} min-w-[200px]`}>
                            {keyField.charAt(0).toUpperCase() + keyField.slice(1)}
                        </TableHead>
                        {months.map((m) => (
                            <TableHead key={m} className="text-right whitespace-nowrap min-w-[70px]">{fmtMonth(m)}</TableHead>
                        ))}
                        <TableHead className="text-right font-bold min-w-[70px]">Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row[keyField] as string}>
                            <TableCell className={`${stickyLeftCellClass} font-medium text-sm`}>{row[keyField] as string}</TableCell>
                            {months.map((m) => {
                                const val = (row[m] as number) || 0;
                                return (
                                    <TableCell key={m} className="text-right tabular-nums text-sm">
                                        {val > 0 ? fmtNum(val, decimals) : <Dash />}
                                    </TableCell>
                                );
                            })}
                            <TableCell className="text-right font-semibold tabular-nums text-sm">{fmtNum(row.total, decimals)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                        <TableCell className="sticky left-0 z-10 bg-muted/30">Total</TableCell>
                        {months.map((m) => {
                            const ct = rows.reduce((s, r) => s + ((r[m] as number) || 0), 0);
                            return <TableCell key={m} className="text-right tabular-nums text-sm">{ct > 0 ? fmtNum(ct, decimals) : "–"}</TableCell>;
                        })}
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(grandTotal, decimals)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

function WeeklyPivotTable({
    rows, weeks, weekStarts, keyField, isFloat = false,
}: {
    rows: EohPivotRow[];
    weeks: string[];
    weekStarts: Record<string, string>;
    keyField: string;
    isFloat?: boolean;
}) {
    const decimals = isFloat ? 1 : 0;
    const grandTotal = rows.reduce((s, r) => s + r.total, 0);
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className={`${stickyLeftHeaderClass} min-w-[200px]`}>
                            {keyField.charAt(0).toUpperCase() + keyField.slice(1)}
                        </TableHead>
                        {weeks.map((w) => (
                            <TableHead key={w} title={`Starts ${fmtDate(weekStarts[w])}`} className="text-right whitespace-nowrap min-w-[64px]">
                                {fmtFyWeek(w)}
                            </TableHead>
                        ))}
                        <TableHead className="text-right font-bold min-w-[70px]">Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row[keyField] as string}>
                            <TableCell className={`${stickyLeftCellClass} font-medium text-sm`}>{row[keyField] as string}</TableCell>
                            {weeks.map((w) => {
                                const val = (row[w] as number) || 0;
                                return (
                                    <TableCell key={w} className="text-right tabular-nums text-sm">
                                        {val > 0 ? fmtNum(val, decimals) : <Dash />}
                                    </TableCell>
                                );
                            })}
                            <TableCell className="text-right font-semibold tabular-nums text-sm">{fmtNum(grandTotal ? row.total : 0, decimals)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                        <TableCell className="sticky left-0 z-10 bg-muted/30">Total</TableCell>
                        {weeks.map((w) => {
                            const ct = rows.reduce((s, r) => s + ((r[w] as number) || 0), 0);
                            return <TableCell key={w} className="text-right tabular-nums text-sm">{ct > 0 ? fmtNum(ct, decimals) : "–"}</TableCell>;
                        })}
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(grandTotal, decimals)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

// ── Weekly fill rate ────────────────────────────────────────

function WeeklyFillTable({rows}: {rows: EohWeeklyRow[]}) {
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[90px]">FY Week</TableHead>
                        <TableHead className="min-w-[90px]">Starts</TableHead>
                        <TableHead className="text-right">Req</TableHead>
                        <TableHead className="text-right">Filled</TableHead>
                        <TableHead className="text-right">Fill %</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.fy_week} className={r.is_future ? "bg-blue-50/50 dark:bg-blue-950/20" : ""}>
                            <TableCell className="font-medium text-sm whitespace-nowrap">
                                {fmtFyWeek(r.fy_week)}
                                {r.is_future && <Badge variant="secondary" className="ml-1 text-[10px] px-1">future</Badge>}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{fmtWeek(r.week)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.requested)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.filled)}</TableCell>
                            <TableCell className="text-right">{pctBadge(r.fill_pct)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtDollar(r.revenue)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

function ClientVolumePeriodTable({
    rows, periods, periodStarts, view,
}: {
    rows: EohClientVolumeRow[];
    periods: string[];
    periodStarts?: Record<string, string>;
    view: "monthly" | "weekly";
}) {
    if (!rows.length) return <p className="text-sm text-muted-foreground">No booked client volume found.</p>;
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className={`${stickyLeftHeaderClass} min-w-[220px]`}>Client</TableHead>
                        {periods.map((p) => (
                            <TableHead
                                key={p}
                                title={view === "weekly" ? `Starts ${fmtDate(periodStarts?.[p])}` : undefined}
                                className="text-right whitespace-nowrap min-w-[82px]"
                            >
                                {view === "weekly" ? fmtFyWeek(p) : fmtMonth(p)}
                            </TableHead>
                        ))}
                        <TableHead className="text-right font-bold min-w-[90px]">Total Hrs</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.client}>
                            <TableCell className={`${stickyLeftCellClass} font-medium text-sm`}>{row.client}</TableCell>
                            {periods.map((p) => {
                                const hours = (row[`${p}_hours`] as number) || 0;
                                const revenue = (row[`${p}_revenue`] as number) || 0;
                                return (
                                    <TableCell key={p} className="text-right tabular-nums text-sm" title={revenue ? `${fmtDollar(revenue)} est. revenue` : undefined}>
                                        {hours > 0 ? fmtNum(hours, 1) : <Dash />}
                                    </TableCell>
                                );
                            })}
                            <TableCell className="text-right font-semibold tabular-nums text-sm">{fmtNum(row.total_hours, 1)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Monthly fill rate ───────────────────────────────────────

function FillRateTable({rows}: {rows: EohFillRateRow[]}) {
    const totReq = rows.reduce((s, r) => s + r.requested, 0);
    const totFill = rows.reduce((s, r) => s + r.filled, 0);
    const totPct = totReq ? Math.round((totFill / totReq) * 1000) / 10 : 0;
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[100px]">Month</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-right">Filled</TableHead>
                        <TableHead className="text-right">Fill Rate</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.month}>
                            <TableCell className="font-medium text-sm">{fmtMonth(r.month)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.requested)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.filled)}</TableCell>
                            <TableCell className="text-right">{pctBadge(r.fill_pct)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totReq)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totFill)}</TableCell>
                        <TableCell className="text-right">{pctBadge(totPct)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

// ── Forward bookings ────────────────────────────────────────

function ForwardBookingsTable({rows}: {rows: EohForwardRow[]}) {
    if (!rows.length) return <p className="text-sm text-muted-foreground">No future booked shifts found.</p>;
    const totShifts = rows.reduce((s, r) => s + r.shifts, 0);
    const totHrs = rows.reduce((s, r) => s + r.hours, 0);
    const totRev = rows.reduce((s, r) => s + r.revenue, 0);
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Shifts</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Est. Revenue</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.month}>
                            <TableCell className="font-medium text-sm">{r.month ? fmtMonth(r.month) : "—"}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.shifts)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtDollar(r.revenue)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totShifts)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totHrs, 1)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtDollar(totRev)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

function ForwardBookingsWeeklyTable({rows}: {rows: EohForwardRow[]}) {
    if (!rows.length) return <p className="text-sm text-muted-foreground">No future booked shifts found.</p>;
    const totShifts = rows.reduce((s, r) => s + r.shifts, 0);
    const totHrs = rows.reduce((s, r) => s + r.hours, 0);
    const totRev = rows.reduce((s, r) => s + r.revenue, 0);
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead>FY Week</TableHead>
                        <TableHead>Starts</TableHead>
                        <TableHead className="text-right">Shifts</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Est. Revenue</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.fy_week}>
                            <TableCell className="font-medium text-sm">{r.fy_week ? fmtFyWeek(r.fy_week) : "—"}</TableCell>
                            <TableCell className="text-sm">{fmtWeek(r.week ?? "")}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.shifts)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtDollar(r.revenue)}</TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-semibold">
                        <TableCell>Total</TableCell>
                        <TableCell />
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totShifts)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtNum(totHrs, 1)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtDollar(totRev)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}

// ── Client concentration ────────────────────────────────────

function ConcentrationTable({rows}: {rows: EohConcentrationRow[]}) {
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[200px]">Client</TableHead>
                        <TableHead className="text-right">Shifts</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">% Share</TableHead>
                        <TableHead className="text-right">Cum. %</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.client}>
                            <TableCell className="font-medium text-sm">{r.client}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.shifts)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtDollar(r.revenue)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{r.pct}%</TableCell>
                            <TableCell className="text-right tabular-nums text-sm font-medium">{r.cumulative_pct}%</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Churn / no-bookings report ──────────────────────────────

function ChurnTable({rows}: {rows: EohChurnRow[]}) {
    if (!rows.length) return <p className="text-sm text-muted-foreground">All clients have booked within the last 30 days.</p>;
    const bands = ["30-day", "60-day", "90-day+"];
    const bandCounts = bands.map((b) => ({band: b, count: rows.filter((r) => r.band === b).length}));
    return (
        <div className="space-y-3">
            <div className="flex gap-3">
                {bandCounts.map((bc) => (
                    <Badge key={bc.band} variant={bc.band === "90-day+" ? "destructive" : bc.band === "60-day" ? "secondary" : "default"}>
                        {bc.band}: {bc.count} clients
                    </Badge>
                ))}
            </div>
            <div className="overflow-x-auto rounded border">
                <Table>
                    <TableHeader className={stickyHeaderClass}>
                        <TableRow className="bg-muted/40">
                            <TableHead className="min-w-[200px]">Client</TableHead>
                            <TableHead className="text-right">Last Booked</TableHead>
                            <TableHead className="text-right">Days Since</TableHead>
                            <TableHead className="text-right">Band</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.client}>
                                <TableCell className="font-medium text-sm">{r.client}</TableCell>
                                <TableCell className="text-right text-sm">{new Date(r.last_booked).toLocaleDateString("en-AU")}</TableCell>
                                <TableCell className="text-right tabular-nums text-sm">{r.days_since}</TableCell>
                                <TableCell className="text-right">
                                    <Badge variant={r.band === "90-day+" ? "destructive" : r.band === "60-day" ? "secondary" : "default"}>
                                        {r.band}
                                    </Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function trendBadge(trend: EohClientTrendRow["trend"]) {
    if (trend === "churned") return <Badge variant="destructive">churned</Badge>;
    if (trend === "declining") return <Badge variant="secondary">declining</Badge>;
    if (trend === "growing") return <Badge variant="default">growing</Badge>;
    return <Badge variant="outline">stable</Badge>;
}

function ClientTrendsTable({rows}: {rows: EohClientTrendRow[]}) {
    const priorityRows = rows.filter((r) => r.trend === "declining" || r.trend === "churned");
    const displayRows = priorityRows.length ? priorityRows : rows.slice(0, 20);
    if (!displayRows.length) return <p className="text-sm text-muted-foreground">No client trend data available yet.</p>;
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[220px]">Client</TableHead>
                        <TableHead>Trend</TableHead>
                        <TableHead className="text-right">Last 13 wk avg</TableHead>
                        <TableHead className="text-right">Prior 13 wk avg</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                        <TableHead className="text-right">Active Wks</TableHead>
                        <TableHead className="text-right">Last Booked</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {displayRows.map((r) => (
                        <TableRow key={r.client}>
                            <TableCell className="font-medium text-sm">{r.client}</TableCell>
                            <TableCell>{trendBadge(r.trend)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.current_weekly_avg_hours, 1)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.prior_period_weekly_avg_hours, 1)}</TableCell>
                            <TableCell className={`text-right tabular-nums text-sm font-medium ${
                                r.pct_change != null && r.pct_change < 0 ? "text-red-600" : ""
                            }`}>
                                {r.pct_change == null ? "—" : `${r.pct_change > 0 ? "+" : ""}${r.pct_change}%`}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{r.weeks_active_last_13}/13</TableCell>
                            <TableCell className="text-right text-sm">{r.last_booked ? new Date(r.last_booked).toLocaleDateString("en-AU") : "—"}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── YoY comparison ──────────────────────────────────────────

function YoYTable({rows, years}: {rows: EohYoYRow[]; years: string[]}) {
    if (!rows.length || years.length < 2) return <p className="text-sm text-muted-foreground">Need at least 2 years of data for YoY comparison.</p>;
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[70px]">Month</TableHead>
                        {years.map((y) => (
                            <TableHead key={y} colSpan={3} className="text-center border-l">{y}</TableHead>
                        ))}
                    </TableRow>
                    <TableRow className="bg-muted/20">
                        <TableHead />
                        {years.map((y) => (
                            <React.Fragment key={y}>
                                <TableHead className="text-right border-l text-xs">Shifts</TableHead>
                                <TableHead className="text-right text-xs">Hours</TableHead>
                                <TableHead className="text-right text-xs">Rev</TableHead>
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.month_num}>
                            <TableCell className="font-medium text-sm">{fmtMonthNum(r.month_num)}</TableCell>
                            {years.map((y) => {
                                const shifts = (r[y + "_shifts"] as number) || 0;
                                const hours = (r[y + "_hours"] as number) || 0;
                                const rev = (r[y + "_revenue"] as number) || 0;
                                return (
                                    <React.Fragment key={y}>
                                        <TableCell className="text-right tabular-nums text-sm border-l">{shifts || <Dash />}</TableCell>
                                        <TableCell className="text-right tabular-nums text-sm">{hours ? fmtNum(hours, 1) : <Dash />}</TableCell>
                                        <TableCell className="text-right tabular-nums text-sm">{rev ? fmtDollar(rev) : <Dash />}</TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Unfill reasons ──────────────────────────────────────────

function UnfillReasonsTable({rows}: {rows: EohUnfillRow[]}) {
    const total = rows.reduce((s, r) => s + r.count, 0);
    return (
        <div className="overflow-x-auto rounded border">
            <Table>
                <TableHeader className={stickyHeaderClass}>
                    <TableRow className="bg-muted/40">
                        <TableHead className="min-w-[300px]">Reason</TableHead>
                        <TableHead className="text-right min-w-[80px]">Count</TableHead>
                        <TableHead className="text-right min-w-[80px]">%</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.reason}>
                            <TableCell className="text-sm">{r.reason}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm">{fmtNum(r.count)}</TableCell>
                            <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                                {total ? Math.round((r.count / total) * 100) : 0}%
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// ── Main tab ────────────────────────────────────────────────

export function BookingsSummaryTab(_props: TabProps) {
    const [view, setView] = React.useState<"monthly" | "weekly">("weekly");
    const {data, isLoading, error} = useEohBookingsSummary();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                Loading bookings data…
            </div>
        );
    }

    if (error) {
        return <div className="py-8 text-center text-sm text-destructive">{error}</div>;
    }

    const months = data?.months ?? [];

    if (!data || months.length === 0) {
        return (
            <div className="py-8 text-center text-sm text-muted-foreground">
                No shift data found. Sync shift data from the Data Sync tab first.
            </div>
        );
    }

    const fyWeeks = data.fy_weeks ?? [];
    const fyWeekStarts = data.fy_week_starts ?? {};
    const hasWeeklyData =
        fyWeeks.length > 0
        && (
            (data.fill_rate_weekly ?? []).length > 0
            || (data.client_volume_weekly ?? []).length > 0
            || (data.hours_by_qualification_weekly ?? []).length > 0
        );
    const effectiveView = view === "weekly" && hasWeeklyData ? "weekly" : "monthly";
    const isWeekly = effectiveView === "weekly";

    return (
        <div className="space-y-8">
            {data.snapshot_kpis && <BookingsSnapshot kpis={data.snapshot_kpis} />}

            <ExecutiveSummary
                summary={data.executive_summary ?? []}
                alerts={data.alerts ?? []}
            />

            {data.chart_series && <BookingsCharts series={data.chart_series} />}

            {(data.daily_activity ?? []).length > 0 && (
                <SectionCard title="Daily Booking Activity" subtitle="Recent and upcoming shift-date activity from the latest EOH dataset.">
                    <DailyActivityTable rows={data.daily_activity} />
                </SectionCard>
            )}

            <div className="flex flex-col gap-3 rounded border bg-background p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-sm font-semibold">Detailed Tables</h3>
                    <p className="text-xs text-muted-foreground">
                        Switch the table breakdowns between FY weeks and calendar months.
                        {!hasWeeklyData ? " Showing monthly because weekly data is unavailable." : ""}
                    </p>
                </div>
                <ToggleGroup
                    type="single"
                    variant="outline"
                    value={effectiveView}
                    onValueChange={(value) => {
                        if (value === "monthly" || value === "weekly") setView(value);
                    }}
                    className="self-start sm:self-auto"
                >
                    <ToggleGroupItem value="weekly" disabled={!hasWeeklyData}>Weekly</ToggleGroupItem>
                    <ToggleGroupItem value="monthly">Monthly</ToggleGroupItem>
                </ToggleGroup>
            </div>

            <SectionCard
                title="Forward Bookings Pipeline"
                subtitle={`Booked shifts from today onwards by ${isWeekly ? "FY week" : "month"} (est. revenue at $${data.charge_rate}/hr)`}
            >
                {isWeekly
                    ? <ForwardBookingsWeeklyTable rows={data.forward_bookings_weekly ?? []} />
                    : <ForwardBookingsTable rows={data.forward_bookings} />}
            </SectionCard>

            <SectionCard
                title={isWeekly ? "Weekly Fill Rate & Revenue" : "Fill Rate by Month"}
                subtitle={`Shifts requested vs filled by ${isWeekly ? "FY week" : "month"}`}
            >
                {isWeekly
                    ? <WeeklyFillTable rows={data.fill_rate_weekly ?? []} />
                    : <FillRateTable rows={data.fill_rate} />}
            </SectionCard>

            <SectionCard
                title="Client Concentration"
                subtitle="Top clients by booked hours — cumulative % shows revenue concentration risk"
            >
                <ConcentrationTable rows={data.client_concentration} />
            </SectionCard>

            <SectionCard
                title="Client Volume"
                subtitle={`Booked client hours by ${isWeekly ? "FY week" : "month"}; hover cells for estimated revenue`}
            >
                <ClientVolumePeriodTable
                    rows={isWeekly ? data.client_volume_weekly ?? [] : data.client_volume}
                    periods={isWeekly ? fyWeeks : months}
                    periodStarts={isWeekly ? fyWeekStarts : undefined}
                    view={view}
                />
            </SectionCard>

            <SectionCard
                title="Client Trends"
                subtitle="Last 13 weeks compared with the prior 13 weeks; declining and churned clients shown first"
            >
                <ClientTrendsTable rows={data.client_trends ?? []} />
            </SectionCard>

            <SectionCard
                title="Hours by Qualification"
                subtitle={`Worked hours split by qualification and ${isWeekly ? "FY week" : "month"}`}
            >
                {isWeekly
                    ? <WeeklyPivotTable rows={data.hours_by_qualification_weekly ?? []} weeks={fyWeeks} weekStarts={fyWeekStarts} keyField="qualification" isFloat />
                    : <PivotTable rows={data.hours_by_qualification} months={months} keyField="qualification" isFloat />}
            </SectionCard>

            <SectionCard
                title="Year-on-Year Comparison"
                subtitle="Same calendar month across years — booked shifts, hours, and estimated revenue"
            >
                <YoYTable rows={data.yoy_comparison} years={data.yoy_years} />
            </SectionCard>

            <SectionCard
                title="No-Bookings Report (Client Churn)"
                subtitle="Clients with no booked shift in 30, 60, or 90+ days"
            >
                <ChurnTable rows={data.churn} />
            </SectionCard>

            <SectionCard
                title="Cancellations by Qualification"
                subtitle={`Cancelled + withdrawn shifts by qualification and ${isWeekly ? "FY week" : "month"}`}
            >
                {isWeekly
                    ? <WeeklyPivotTable rows={data.cancellations_by_qualification_weekly ?? []} weeks={fyWeeks} weekStarts={fyWeekStarts} keyField="qualification" />
                    : <PivotTable rows={data.cancellations_by_qualification} months={months} keyField="qualification" />}
            </SectionCard>

            <SectionCard
                title="Cancelled Shifts by Client"
                subtitle="Top clients by cancelled + withdrawn shifts"
            >
                {isWeekly
                    ? <WeeklyPivotTable rows={data.cancellations_by_client_weekly ?? []} weeks={fyWeeks} weekStarts={fyWeekStarts} keyField="client" />
                    : <PivotTable rows={data.cancellations_by_client} months={months} keyField="client" />}
            </SectionCard>

            <SectionCard
                title="Booking Status Breakdown"
                subtitle={`Shift count by status and ${isWeekly ? "FY week" : "month"}`}
            >
                {isWeekly
                    ? <WeeklyPivotTable rows={data.status_funnel_weekly ?? []} weeks={fyWeeks} weekStarts={fyWeekStarts} keyField="status" />
                    : <PivotTable rows={data.status_funnel} months={months} keyField="status" />}
            </SectionCard>

            <SectionCard
                title="Booking Status Hours"
                subtitle={`Shift hours by status and ${isWeekly ? "FY week" : "month"}`}
            >
                {isWeekly
                    ? <WeeklyPivotTable rows={data.status_hours_weekly ?? []} weeks={fyWeeks} weekStarts={fyWeekStarts} keyField="status" isFloat />
                    : <PivotTable rows={data.status_hours} months={months} keyField="status" isFloat />}
            </SectionCard>

            <SectionCard
                title="Top Unfill / Cancellation Reasons"
                subtitle="Most common reasons for cancelled, withdrawn, or unfilled shifts"
            >
                <UnfillReasonsTable rows={data.unfill_reasons} />
            </SectionCard>
        </div>
    );
}
